import { TFunction } from 'i18next';
import { listingShareUrl } from './shareUrls';

interface ShareOffMarketWillingnessParams {
  // Kept for callers that still describe the willingness by building; the
  // share URL itself resolves off the event id alone.
  buildingId?: string;
  eventId: string;
  isSellInterest: boolean;
  address: {
    street?: string;
    houseNumber?: string;
    postcode?: string;
    city?: string;
  };
  t: TFunction;
  isMatch?: boolean;
}

interface ShareUrlWithFallbackParams {
  url: string;
  t: TFunction;
  title?: string;
  text?: string;
}

/**
 * Shares a URL with native share when available, then falls back to copying.
 * The textarea path keeps local/non-secure testing environments observable.
 */
export const shareUrlWithFallback = async ({
  url,
  t,
  title,
  text,
}: ShareUrlWithFallbackParams): Promise<boolean> => {
  if (navigator.share) {
    try {
      await navigator.share({
        ...(title ? { title } : {}),
        ...(text ? { text } : {}),
        url,
      });
      return true;
    } catch (error) {
      console.error('Error sharing:', error);

      if (error instanceof Error && error.name === 'AbortError') {
        return false;
      }
    }
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
      return true;
    } catch (err) {
      console.error('Could not copy text: ', err);
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = url;
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '0';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');

    if (successful) {
      alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
      return true;
    }

    alert(t('shareButton.linkCopiedError', 'Could not copy link'));
    return false;
  } catch (err) {
    console.error('Fallback: Unable to copy', err);
    alert(t('shareButton.linkCopiedError', 'Could not copy link'));
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
};

/**
 * Shares an off-market buy or sell willingness event
 * @param params - The parameters needed for sharing
 * @returns Promise<boolean> - true if sharing was successful, false otherwise
 */
export const shareOffMarketWillingness = async ({
  eventId,
  t,
}: ShareOffMarketWillingnessParams): Promise<boolean> => {
  try {
    // A willingness *is* the listing (same _id), so it shares the same URL as
    // the listing hero and cards. Earlier this pointed at
    // `${REACT_APP_API_URL}/share-event`, i.e. api.jaakko.com — a second host
    // for the same content, and one whose preview landed on the map instead of
    // the listing view.
    return await shareUrlWithFallback({ url: listingShareUrl(eventId), t });
  } catch (error) {
    console.error('Error in shareOffMarketWillingness:', error);
    return false;
  }
};

interface ShareListingParams {
  // Human-readable address used only for the clipboard "copied" feedback; the
  // shared payload itself is just the URL for cleaner previews.
  address?: string;
  // Optional override; defaults to the SPA origin (www.jaakko.com in prod).
  // The Add my place flow is visual-only (no real listing URL exists yet), so
  // we share a generic link best-effort.
  url?: string;
  t: TFunction;
}

/**
 * Shares a (visual-only) listing using the native share sheet, falling back to
 * the clipboard. Mirrors {@link shareOffMarketWillingness} but without a real
 * building/event id.
 * @returns Promise<boolean> - true if sharing/copying succeeded, false otherwise
 */
export const shareListing = async ({
  url,
  t,
}: ShareListingParams): Promise<boolean> => {
  // The apex jaakko.com only redirects its root; www is where the site lives.
  const shareUrl = url || `${window.location.origin}/`;

  // Try native sharing first.
  if (navigator.share) {
    try {
      await navigator.share({ url: shareUrl });
      return true;
    } catch (error) {
      console.error('Error sharing:', error);
      // Don't fall through to clipboard if the user cancelled the sheet.
      if (error instanceof Error && error.name === 'AbortError') {
        return false;
      }
    }
  }

  // Fallback to clipboard.
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
      return true;
    } catch (err) {
      console.error('Could not copy text: ', err);
    }
  }

  // Fallback for older browsers.
  const textArea = document.createElement('textarea');
  textArea.value = shareUrl;
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '0';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (successful) {
      alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
      return true;
    }
    alert(t('shareButton.linkCopiedError', 'Could not copy link'));
    return false;
  } catch (err) {
    console.error('Fallback: Unable to copy', err);
    document.body.removeChild(textArea);
    alert(t('shareButton.linkCopiedError', 'Could not copy link'));
    return false;
  }
};

/**
 * Formats an address object into a readable string
 * @param address - The address object
 * @returns A formatted address string
 */
export const formatAddressString = (address: {
  street?: string;
  houseNumber?: string;
  postcode?: string;
  city?: string;
  country?: string;
}): string => {
  const parts = [];

  if (address.street) {
    parts.push(address.houseNumber ? `${address.street} ${address.houseNumber}` : address.street);
  }

  if (address.city) {
    parts.push(address.postcode ? `${address.postcode} ${address.city}` : address.city);
  }

  if (address.country) {
    parts.push(address.country);
  }

  return parts.join(', ');
};
